import { plainToInstance, Type } from 'class-transformer'
import i18n from 'vue-i18n'

import { MenuSectionNames, SocketNames } from '../../../../../components'
import { BaseNodeModelData } from '../../../BaseNode/BaseNodeDataModel'
import { BaseNodeModel } from '../../../BaseNode/BaseNodeModel'
import { CustomNode, CustomNodeComponent, NodeTypes } from '../../../index'
import ConditionsSampleNode from '../ui/SampleNode/ConditionsSampleNode.vue'
import ConditionsNodeSidebar from '../ui/Sidebar/ConditionsNodeSidebar.vue'
import BaseCondition from './ConditionTypes/BaseCondition'
import { ConditionsGroup, ConditionsNodeProps } from './types'

export default class ConditionsNodeBuilder extends CustomNodeComponent {
  sidebarComponent = ConditionsNodeSidebar

  constructor(i18n: i18n, nodeProps: ConditionsNodeProps) {
    super(ConditionsSampleNode, ConditionsNode, NodeTypes.Conditions, i18n, nodeProps)
  }

  getBaseNodeModel = ([ x, y ]: [number, number]) => {
    return {
      data: plainToInstance(ConditionsNodeData, {
        type: NodeTypes.Conditions,
        groups: [],
        fail_output: CustomNode.LastSocketId += 1
      }),
      input: CustomNode.LastSocketId += 1,
      color: null,
      x,
      y,
    }
  }
}

export class ConditionsNode extends CustomNode {
  data: ConditionsNodeData

  declare nodeProps: ConditionsNodeProps
  constructor(name: NodeTypes.Conditions, model: BaseNodeModel, nodeProps: ConditionsNodeProps) {
    super(name, model, nodeProps)

    if (model.data instanceof ConditionsNodeData) {
      model.data.groups.forEach(({ success_output }) => this.socketActions.createOutput(success_output, SocketNames.Success))
      this.socketActions.createOutput(model.data.fail_output, SocketNames.Fail)

      this.data = model.data
    } else {
      throw new Error('To node come unknown data')
    }
  }

  removeGroup(guid: string): void {
    const group = this.data.groups.find((g) => g.guid === guid)

    if (group) {
      const groupIndex = this.data.groups.indexOf(group)

      if (groupIndex !== -1) {
        this.socketActions.deleteOutput(group.success_output)

        this.data.groups.splice(groupIndex, 1)
      }
    }
  }

  removeCondition(conditionGuid: string, groupGuid: string): void {
    const group = this.data.groups.find((g) => g.guid === groupGuid)

    if (group) {
      const index = group.conditions.findIndex((c) => c.guid === conditionGuid)

      if (index !== -1) {
        group.conditions.splice(index, 1)
      }

      if (group.conditions.length === 0) {
        this.socketActions.deleteOutput(group.success_output)

        this.data.groups.splice(this.data.groups.indexOf(group), 1)
      }
    }
  }

  spliceCondition(newCondition: BaseCondition, conditionGuid: string, groupGuid: string): void {
    const group = this.data.groups.find((g) => g.guid === groupGuid)

    if (group) {
      const condition = group.conditions.find((c) => c.guid === conditionGuid)

      if (condition) {
        const index = group.conditions.findIndex(c => c.guid === condition.guid)

        if (index !== -1) {
          group.conditions.splice(index, 1, newCondition)
        }
      }
    }
  }

  setCondition(condition: BaseCondition, groupGuid?: string): void {
    if (groupGuid) {
      const group = this.data.groups.find((g) => g.guid === groupGuid)

      if (group) {
        group.conditions.push(condition)
      }
    } else {
      const output = this.socketActions.createOutput(CustomNode.LastSocketId + 1, SocketNames.Success)
      CustomNode.LastSocketId += 1

      this.data.groups.push(plainToInstance(ConditionsGroup, {
        success_output: output,
        conditions: [ condition ],
      }))
    }
  }
}

export class ConditionsNodeData extends BaseNodeModelData {
  declare type: NodeTypes.Conditions
  fail_output!: number
  @Type(() => ConditionsGroup)
  groups!: Array<ConditionsGroup>
}
